import styled, { css } from 'styled-components';
import { colors } from 'syngenta-digital-cropwise-react-ui-kit';

import IconDropletSize from 'components/Icons/IconDropletSize';
import IconThermostat from 'components/Icons/IconThermostat';
import breakpoints from 'base/constants/breakpoints';

interface BarChartLabelProps {
  isPdfView?: boolean;
}

const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const Container = styled(TextContainer)<BarChartLabelProps>`
  overflow: hidden;
  justify-content: start;
  padding: 0 15px;
  position: relative;

  ${({ isPdfView }) =>
    !isPdfView &&
    css`
      @media only screen and ${breakpoints.maxWidth.xl} {
        padding: 0 0;
      }

      @media only screen and ${breakpoints.maxWidth.sm} {
        padding: 0 2px;
        transform: translate(-50%) scale(0.9);
      }

      @media only screen and ${breakpoints.maxWidth.xs} {
        transform: translate(-50%) scale(0.8);
        padding: 0 1px;
      }
    `}

  ${({ isPdfView }) =>
    isPdfView &&
    css`
      transform: translate(0);
      padding: 0 0;
    `}
`;

const StyledTitle = styled.h1<{ color: string } & BarChartLabelProps>`
  font-size: 16px;
  font-weight: bold;
  text-align: center;
  color: ${({ color }) => color};

  ${({ isPdfView }) =>
    !isPdfView &&
    css`
      @media only screen and ${breakpoints.minWidth.sm} and ${breakpoints.maxWidth.xl} {
        font-size: 14px;
      }

      @media only screen and ${breakpoints.maxWidth.sm} {
        font-size: 12px;
      }

      @media only screen and ${breakpoints.maxWidth.xs} {
        font-size: 11px;
      }
    `}
`;

const StyledPrecipitationContainer = styled.div<BarChartLabelProps>`
  display: flex;
  align-self: start;
  align-items: center;
  font-size: 14px;

  ${({ isPdfView }) =>
    !isPdfView &&
    css`
      @media only screen and ${breakpoints.minWidth.sm} and ${breakpoints.maxWidth.xl} {
        font-size: 12px;
      }

      @media only screen and ${breakpoints.maxWidth.sm} {
        align-items: start;
        font-size: 11px;
      }

      @media only screen and ${breakpoints.maxWidth.xs} {
        font-size: 10px;
      }
    `}
`;

const StyledTemperatureContainer = styled(StyledPrecipitationContainer)`
  margin-bottom: 2px;
`;

const StyledTemperatureTitleContainer = styled(StyledPrecipitationContainer)`
  display: flex;
  justify-content: center;
  margin-bottom: 2px;
`;

const StyledTemperatureBoldText = styled.div`
  font-style: normal;
  font-weight: 700;
  font-size: 14px;
  line-height: 22px;
  letter-spacing: -0.0075em;

  @media only screen and ${breakpoints.maxWidth.sm} {
    font-size: 12px;
    line-height: 18px;
  }

  @media only screen and ${breakpoints.maxWidth.xs} {
    font-size: 11px;
    line-height: 16px;
  }
`;

const StyledIconDropletSize = styled(IconDropletSize)<BarChartLabelProps>`
  width: 15px;
  margin-right: 8px;

  ${({ isPdfView }) =>
    !isPdfView &&
    css`
      @media only screen and ${breakpoints.maxWidth.sm} {
        margin-top: 5px;
        width: 12px;
        margin-right: 6px;
      }

      @media only screen and ${breakpoints.maxWidth.xs} {
        width: 10px;
        margin-right: 4px;
      }
    `}
`;

const StyledIconThermostat = styled(IconThermostat)<BarChartLabelProps>`
  width: 15px;
  margin-right: 8px;

  ${({ isPdfView }) =>
    !isPdfView &&
    css`
      @media only screen and ${breakpoints.maxWidth.sm} {
        margin-top: 5px;
        width: 12px;
        margin-right: 6px;
      }

      @media only screen and ${breakpoints.maxWidth.xs} {
        width: 10px;
        margin-right: 4px;
      }
    `}
`;

const StyledYearsContainer = styled(TextContainer)`
  flex-direction: row;
  width: 100%;
  gap: 8px;
  margin-top: 16px;
`;

const StyledYearRectangle = styled.div<{ color: string }>`
  display: inline-flex;
  min-width: 24px;
  height: 24px;
  border-radius: 4px;
  background-color: ${({ color }) => color};

  @media only screen and ${breakpoints.maxWidth.sm} {
    min-width: 20px;
    height: 20px;
    border-radius: 3px;
  }

  @media only screen and ${breakpoints.maxWidth.xs} {
    min-width: 16px;
    height: 16px;
    border-radius: 2px;
  }
`;

const StyledYearText = styled.div<BarChartLabelProps>`
  display: inline-flex;
  font-size: 16px;
  color: ${colors.neutral90};

  ${({ isPdfView }) =>
    !isPdfView &&
    css`
      @media only screen and ${breakpoints.minWidth.sm} and ${breakpoints.maxWidth.xl} {
        font-size: 14px;
      }

      @media only screen and ${breakpoints.maxWidth.sm} {
        font-size: 12px;
      }

      @media only screen and ${breakpoints.maxWidth.xs} {
        font-size: 11px;
      }
    `}
`;

export {
  Container,
  TextContainer,
  StyledTitle,
  StyledTemperatureContainer,
  StyledPrecipitationContainer,
  StyledIconDropletSize,
  StyledIconThermostat,
  StyledYearsContainer,
  StyledYearRectangle,
  StyledYearText,
  StyledTemperatureTitleContainer,
  StyledTemperatureBoldText,
};
